import { get, post, put } from '@/lib/api/client'
import {
  PaginationParamsSchema,
  PressReleaseFilterParamsSchema,
  type PressRelease,
  type PressReleaseDetail,
  type PressReleaseFilterParams,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class PressReleaseService {
  /**
   * Filter press releases with pagination (Public endpoint)
   * POST /press-release/public/filter?page={page}&size={size}
   */
  async getPressReleases(
    params: PaginationParams = { page: 1, size: 10 },
    filters: PressReleaseFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = PressReleaseFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<PressRelease>>>(
      `press-release/public/filter?${query}`,
      validatedFilters
    )
  }

  /**
   * Search press releases (Public endpoint)
   * GET /press-release/public/search?searchBy={searchTerm}&size={size}&page={page}
   */
  async searchPressReleases(
    searchTerm: string,
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const query = new URLSearchParams({
      searchBy: searchTerm,
      size: validatedParams.size.toString(),
      page: validatedParams.page.toString()
    }).toString()

    return await get<ApiResponse<PaginatedResponse<PressRelease>>>(
      `press-release/public/search?${query}`
    )
  }

  /**
   * Get press release by ID (Public endpoint)
   * GET /press-release/public/{pressReleaseId}
   */
  async getPressReleaseById(pressReleaseId: string): Promise<ApiResponse<PressReleaseDetail>> {
    return await get<ApiResponse<PressReleaseDetail>>(
      `press-release/public/${pressReleaseId}`
    )
  }

  /**
   * Get featured press releases (banner)
   * POST /press-release/public/filter with pressReleaseBanner filter
   */
  async getFeaturedPressReleases(
    params: PaginationParams = { page: 1, size: 3 }
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    return this.getPressReleases(params, {
      landingPage: true,
      pressRelease: true,
      pressReleaseBanner: true
    })
  }

  /**
   * Get landing page press releases
   * POST /press-release/public/filter with landingPage filter
   */
  async getLandingPagePressReleases(
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    return this.getPressReleases(params, {
      landingPage: true,
      pressRelease: true,
      pressReleaseBanner: false
    })
  }

  /**
   * Get all published press releases
   * POST /press-release/public/filter with pressRelease filter
   */
  async getPublishedPressReleases(
    params: PaginationParams = { page: 1, size: 10 },
    filters: PressReleaseFilterParams
  ): Promise<ApiResponse<PaginatedResponse<PressRelease>>> {
    return this.getPressReleases(params, filters)
  }

  /**
   * Increment view count for a press release
   * PUT /press-release/public/{pressReleaseId}/increment-view
   */
  async incrementView(pressReleaseId: string): Promise<ApiResponse<void>> {
    return await put<ApiResponse<void>>(
      `press-release/public/${pressReleaseId}/increment-view`
    )
  }

  /**
   * Get all press releases (public endpoint)
   * GET /press-release/public/all
   */
  async getAllPressReleases(): Promise<ApiResponse<PressRelease[]>> {
    return await get<ApiResponse<PressRelease[]>>(
      'press-release/public/all'
    )
  }

  /**
   * Get home page highlights
   * GET /press-release/public/highlights?type={type}
   */
  async getHomePageHighlights(type: string = 'HOME_PAGE_HERO'): Promise<ApiResponse<PressRelease[]>> {
    return await get<ApiResponse<PressRelease[]>>(
      `press-release/public/highlights?type=${type}`
    )
  }
}

// Export singleton instance
export const pressReleaseService = new PressReleaseService()

// Export default
export default pressReleaseService
